interface Point {
  x: number;
  y: number;
}

interface Line {
  rho: number;
  theta: number;
  votes: number;
}

// Convert image to grayscale
function toGrayscale(imageData: ImageData): Uint8ClampedArray {
  const gray = new Uint8ClampedArray(imageData.width * imageData.height);
  const data = imageData.data;
  
  for (let i = 0; i < gray.length; i++) {
    const idx = i * 4;
    gray[i] = Math.round(0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2]);
  }
  
  return gray;
}

// Apply contrast enhancement (simplified CLAHE-like)
function enhanceContrast(gray: Uint8ClampedArray): Uint8ClampedArray {
  // Calculate histogram
  const histogram = new Uint32Array(256);
  for (let i = 0; i < gray.length; i++) {
    histogram[gray[i]]++;
  }
  
  // Calculate CDF
  const cdf = new Uint32Array(256);
  cdf[0] = histogram[0];
  for (let i = 1; i < 256; i++) {
    cdf[i] = cdf[i - 1] + histogram[i];
  }
  
  // Find min/max CDF for stretching
  let cdfMin = 0;
  for (let i = 0; i < 256; i++) {
    if (cdf[i] > 0) {
      cdfMin = cdf[i];
      break;
    }
  }
  const cdfMax = cdf[255];
  
  // Apply histogram equalization
  const enhanced = new Uint8ClampedArray(gray.length);
  const scale = 255 / (cdfMax - cdfMin || 1);
  
  for (let i = 0; i < gray.length; i++) {
    enhanced[i] = Math.round((cdf[gray[i]] - cdfMin) * scale);
  }
  
  return enhanced;
}

// Apply Gaussian blur (5x5 kernel)
function gaussianBlur(gray: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const kernel = [
    [1, 4, 7, 4, 1],
    [4, 16, 26, 16, 4],
    [7, 26, 41, 26, 7],
    [4, 16, 26, 16, 4],
    [1, 4, 7, 4, 1]
  ];
  const kernelSum = 273;
  const blurred = new Uint8ClampedArray(gray.length);
  
  // Copy edges
  for (let i = 0; i < gray.length; i++) {
    blurred[i] = gray[i];
  }
  
  for (let y = 2; y < height - 2; y++) {
    for (let x = 2; x < width - 2; x++) {
      let sum = 0;
      for (let ky = -2; ky <= 2; ky++) {
        for (let kx = -2; kx <= 2; kx++) {
          sum += gray[(y + ky) * width + (x + kx)] * kernel[ky + 2][kx + 2];
        }
      }
      blurred[y * width + x] = Math.round(sum / kernelSum);
    }
  }
  
  return blurred;
}

// Sobel edge detection with improved thresholds
function sobelEdgeDetection(gray: Uint8ClampedArray, width: number, height: number): Float32Array {
  const sobelX = [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]];
  const sobelY = [[-1, -2, -1], [0, 0, 0], [1, 2, 1]];
  
  const magnitude = new Float32Array(gray.length);
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let gx = 0;
      let gy = 0;
      
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const pixel = gray[(y + ky) * width + (x + kx)];
          gx += pixel * sobelX[ky + 1][kx + 1];
          gy += pixel * sobelY[ky + 1][kx + 1];
        }
      }
      
      magnitude[y * width + x] = Math.sqrt(gx * gx + gy * gy);
    }
  }
  
  return magnitude;
}

// Adaptive threshold based on percentile
function adaptiveThreshold(magnitude: Float32Array, percentile: number): Uint8ClampedArray {
  // Collect non-zero values
  const values: number[] = [];
  for (let i = 0; i < magnitude.length; i++) {
    if (magnitude[i] > 0) {
      values.push(magnitude[i]);
    }
  }
  
  if (values.length === 0) {
    return new Uint8ClampedArray(magnitude.length);
  }
  
  // Sort and find threshold
  values.sort((a, b) => a - b);
  const thresholdIdx = Math.floor(values.length * (1 - percentile));
  const threshold = values[Math.max(0, thresholdIdx)];
  
  const edges = new Uint8ClampedArray(magnitude.length);
  for (let i = 0; i < magnitude.length; i++) {
    edges[i] = magnitude[i] >= threshold ? 255 : 0;
  }
  
  return edges;
}

// Morphological dilation to connect edges
function dilate(edges: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray {
  const result = new Uint8ClampedArray(edges.length);
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      if (edges[idx] === 255) {
        result[idx] = 255;
        continue;
      }
      
      // Check 4-connected neighbors
      if (edges[(y - 1) * width + x] === 255 ||
          edges[(y + 1) * width + x] === 255 ||
          edges[y * width + (x - 1)] === 255 ||
          edges[y * width + (x + 1)] === 255) {
        result[idx] = 255;
      }
    }
  }
  
  return result;
}

// Hough transform for line detection
function houghLines(edges: Uint8ClampedArray, width: number, height: number): Line[] {
  const diagonal = Math.sqrt(width * width + height * height);
  const rhoMax = Math.ceil(diagonal);
  const thetaSteps = 180;
  
  // Accumulator array
  const accumulator = new Uint32Array(2 * rhoMax * thetaSteps);
  
  // Precompute sin/cos
  const sinTable: number[] = [];
  const cosTable: number[] = [];
  for (let t = 0; t < thetaSteps; t++) {
    const theta = (t * Math.PI) / thetaSteps;
    sinTable.push(Math.sin(theta));
    cosTable.push(Math.cos(theta));
  }
  
  // Vote
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (edges[y * width + x] !== 255) continue;
      
      for (let t = 0; t < thetaSteps; t++) {
        const rho = Math.round(x * cosTable[t] + y * sinTable[t]);
        const rhoIdx = rho + rhoMax;
        accumulator[rhoIdx * thetaSteps + t]++;
      }
    }
  }
  
  // Find peaks
  const lines: Line[] = [];
  const minVotes = Math.min(width, height) * 0.15; // At least 15% of smaller dimension
  
  for (let rhoIdx = 0; rhoIdx < 2 * rhoMax; rhoIdx++) {
    for (let t = 0; t < thetaSteps; t++) {
      const votes = accumulator[rhoIdx * thetaSteps + t];
      if (votes >= minVotes) {
        // Local maximum check (simplified)
        let isMax = true;
        for (let dr = -5; dr <= 5 && isMax; dr++) {
          for (let dt = -5; dt <= 5 && isMax; dt++) {
            if (dr === 0 && dt === 0) continue;
            const nr = rhoIdx + dr;
            const nt = (t + dt + thetaSteps) % thetaSteps;
            if (nr >= 0 && nr < 2 * rhoMax) {
              if (accumulator[nr * thetaSteps + nt] > votes) {
                isMax = false;
              }
            }
          }
        }
        
        if (isMax) {
          lines.push({
            rho: rhoIdx - rhoMax,
            theta: (t * Math.PI) / thetaSteps,
            votes
          });
        }
      }
    }
  }
  
  return lines.sort((a, b) => b.votes - a.votes).slice(0, 20);
}

// Classify lines as horizontal or vertical
function classifyLines(lines: Line[]): { horizontal: Line[]; vertical: Line[] } {
  const horizontal: Line[] = [];
  const vertical: Line[] = [];
  
  for (const line of lines) {
    // Theta near 0 or PI = vertical, theta near PI/2 = horizontal
    const thetaDeg = (line.theta * 180) / Math.PI;
    if (thetaDeg < 30 || thetaDeg > 150) {
      vertical.push(line);
    } else if (thetaDeg > 60 && thetaDeg < 120) {
      horizontal.push(line);
    }
  }
  
  return { horizontal, vertical };
}

// Find intersection of two lines
function lineIntersection(line1: Line, line2: Line): Point | null {
  const cos1 = Math.cos(line1.theta);
  const sin1 = Math.sin(line1.theta);
  const cos2 = Math.cos(line2.theta);
  const sin2 = Math.sin(line2.theta);
  
  const det = cos1 * sin2 - cos2 * sin1;
  if (Math.abs(det) < 0.01) return null; // Parallel lines
  
  const x = (line1.rho * sin2 - line2.rho * sin1) / det;
  const y = (line2.rho * cos1 - line1.rho * cos2) / det;
  
  return { x, y };
}

// Find quadrilateral from lines, preferring rectangle around image center
function findQuadFromLines(lines: Line[], width: number, height: number): Point[] | null {
  const { horizontal, vertical } = classifyLines(lines);
  
  if (horizontal.length < 2 || vertical.length < 2) {
    return null;
  }
  
  const centerY = height / 2;
  const centerX = width / 2;

  // For horizontal lines, choose one above and one below center, closest to center
  let topLine: Line | null = null;
  let bottomLine: Line | null = null;
  let topDist = Infinity;
  let bottomDist = Infinity;

  for (const line of horizontal) {
    const y = line.rho / Math.sin(line.theta || 0.001);
    if (y < centerY) {
      const d = Math.abs(centerY - y);
      if (d < topDist) {
        topDist = d;
        topLine = line;
      }
    } else {
      const d = Math.abs(centerY - y);
      if (d < bottomDist) {
        bottomDist = d;
        bottomLine = line;
      }
    }
  }

  // For vertical lines, choose one left and one right of center, closest to center
  let leftLine: Line | null = null;
  let rightLine: Line | null = null;
  let leftDist = Infinity;
  let rightDist = Infinity;

  for (const line of vertical) {
    const x = line.rho / Math.cos(line.theta || 0.001);
    if (x < centerX) {
      const d = Math.abs(centerX - x);
      if (d < leftDist) {
        leftDist = d;
        leftLine = line;
      }
    } else {
      const d = Math.abs(centerX - x);
      if (d < rightDist) {
        rightDist = d;
        rightLine = line;
      }
    }
  }

  if (!topLine || !bottomLine || !leftLine || !rightLine) {
    return null;
  }
  
  // Find 4 corners
  const topLeft = lineIntersection(topLine, leftLine);
  const topRight = lineIntersection(topLine, rightLine);
  const bottomRight = lineIntersection(bottomLine, rightLine);
  const bottomLeft = lineIntersection(bottomLine, leftLine);
  
  if (!topLeft || !topRight || !bottomRight || !bottomLeft) {
    return null;
  }
  
  const corners = [topLeft, topRight, bottomRight, bottomLeft];
  
  // Validate corners are within image bounds (with some tolerance)
  const margin = Math.max(width, height) * 0.1;
  for (const corner of corners) {
    if (
      corner.x < -margin ||
      corner.x > width + margin ||
      corner.y < -margin ||
      corner.y > height + margin
    ) {
      return null;
    }
  }
  
  // Clamp to image bounds
  return corners.map((c) => ({
    x: Math.max(0, Math.min(width, c.x)),
    y: Math.max(0, Math.min(height, c.y)),
  }));
}

// Find document by looking for bright rectangular area using contour-like approach
function findBrightRectangle(gray: Uint8ClampedArray, width: number, height: number): Point[] | null {
  // Compute global brightness statistics
  let sum = 0;
  let maxVal = 0;
  let minVal = 255;
  for (let i = 0; i < gray.length; i++) {
    const v = gray[i];
    sum += v;
    if (v > maxVal) maxVal = v;
    if (v < minVal) minVal = v;
  }
  const globalMean = sum / (gray.length || 1);
  const range = maxVal - minVal;

  // If low contrast image, lower threshold requirements
  const contrastFactor = range > 100 ? 0.5 : 0.3;
  
  // Calculate average brightness in grid cells (finer grid for better precision)
  const gridSize = 30;
  const cellW = Math.floor(width / gridSize);
  const cellH = Math.floor(height / gridSize);
  
  if (cellW < 1 || cellH < 1) return null;
  
  const brightGrid = new Float32Array(gridSize * gridSize);
  
  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      let cellSum = 0;
      let count = 0;
      
      for (let y = gy * cellH; y < Math.min((gy + 1) * cellH, height); y++) {
        for (let x = gx * cellW; x < Math.min((gx + 1) * cellW, width); x++) {
          const idx = y * width + x;
          if (idx >= 0 && idx < gray.length) {
            cellSum += gray[idx];
            count++;
          }
        }
      }
      
      brightGrid[gy * gridSize + gx] = count > 0 ? cellSum / count : 0;
    }
  }
  
  // Dynamic threshold based on image characteristics
  const threshold = globalMean + (maxVal - globalMean) * contrastFactor;
  
  // Find bounding box of bright region using flood-fill like approach
  let minGX = gridSize, maxGX = 0, minGY = gridSize, maxGY = 0;
  let foundBright = false;
  
  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      if (brightGrid[gy * gridSize + gx] >= threshold) {
        foundBright = true;
        if (gx < minGX) minGX = gx;
        if (gx > maxGX) maxGX = gx;
        if (gy < minGY) minGY = gy;
        if (gy > maxGY) maxGY = gy;
      }
    }
  }
  
  if (!foundBright || maxGX <= minGX || maxGY <= minGY) {
    return null;
  }
  
  // Verify the found rectangle has reasonable coverage
  let brightCount = 0;
  let totalCount = 0;
  for (let gy = minGY; gy <= maxGY; gy++) {
    for (let gx = minGX; gx <= maxGX; gx++) {
      totalCount++;
      if (brightGrid[gy * gridSize + gx] >= threshold) {
        brightCount++;
      }
    }
  }
  
  const coverage = brightCount / (totalCount || 1);
  if (coverage < 0.5) {
    return null;
  }
  
  // Check aspect ratio (should be roughly document-like)
  const widthCells = maxGX - minGX + 1;
  const heightCells = maxGY - minGY + 1;
  const aspect = widthCells / heightCells;
  if (aspect < 0.4 || aspect > 2.5) {
    return null;
  }
  
  // Convert grid coordinates back to pixels with small margin inward
  const marginX = cellW * 0.5;
  const marginY = cellH * 0.5;
  
  return [
    { x: Math.max(0, minGX * cellW + marginX), y: Math.max(0, minGY * cellH + marginY) },
    { x: Math.min(width, (maxGX + 1) * cellW - marginX), y: Math.max(0, minGY * cellH + marginY) },
    { x: Math.min(width, (maxGX + 1) * cellW - marginX), y: Math.min(height, (maxGY + 1) * cellH - marginY) },
    { x: Math.max(0, minGX * cellW + marginX), y: Math.min(height, (maxGY + 1) * cellH - marginY) },
  ];
}

// Calculate quadrilateral area
function quadArea(quad: Point[]): number {
  let area = 0;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    area += quad[i].x * quad[j].y;
    area -= quad[j].x * quad[i].y;
  }
  return Math.abs(area) / 2;
}

// Check if quadrilateral is convex
function isConvexQuad(quad: Point[]): boolean {
  let sign = 0;
  
  for (let i = 0; i < 4; i++) {
    const p1 = quad[i];
    const p2 = quad[(i + 1) % 4];
    const p3 = quad[(i + 2) % 4];
    
    const cross = (p2.x - p1.x) * (p3.y - p2.y) - (p2.y - p1.y) * (p3.x - p2.x);
    
    if (sign === 0) {
      sign = cross > 0 ? 1 : -1;
    } else if ((cross > 0 && sign < 0) || (cross < 0 && sign > 0)) {
      return false;
    }
  }
  
  return true;
}

// Expand detected quad so we capture the full document, not just the inner content
function expandQuad(quad: Point[], width: number, height: number, factor = 1.3): Point[] {
  if (quad.length !== 4) return quad;

  // Compute centroid
  const center = quad.reduce(
    (acc, p) => ({ x: acc.x + p.x / 4, y: acc.y + p.y / 4 }),
    { x: 0, y: 0 }
  );

  return quad.map((p) => {
    const dx = p.x - center.x;
    const dy = p.y - center.y;
    const newX = Math.max(0, Math.min(width, center.x + dx * factor));
    const newY = Math.max(0, Math.min(height, center.y + dy * factor));
    return { x: newX, y: newY };
  });
}

// Use row/column edge projections to find document rectangle
function findQuadFromProjections(
  edges: Uint8ClampedArray,
  width: number,
  height: number
): Point[] | null {
  const rowCounts = new Uint32Array(height);
  const colCounts = new Uint32Array(width);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (edges[y * width + x] === 255) {
        rowCounts[y]++;
        colCounts[x]++;
      }
    }
  }

  const rowThreshold = width * 0.15; // need a long horizontal edge
  const colThreshold = height * 0.15; // need a long vertical edge
  const marginY = Math.floor(height * 0.05);
  const marginX = Math.floor(width * 0.05);

  let top = -1;
  for (let y = marginY; y < height - marginY; y++) {
    if (rowCounts[y] >= rowThreshold) {
      top = y;
      break;
    }
  }

  let bottom = -1;
  for (let y = height - marginY - 1; y >= marginY; y--) {
    if (rowCounts[y] >= rowThreshold) {
      bottom = y;
      break;
    }
  }

  let left = -1;
  for (let x = marginX; x < width - marginX; x++) {
    if (colCounts[x] >= colThreshold) {
      left = x;
      break;
    }
  }

  let right = -1;
  for (let x = width - marginX - 1; x >= marginX; x--) {
    if (colCounts[x] >= colThreshold) {
      right = x;
      break;
    }
  }

  if (top === -1 || bottom === -1 || left === -1 || right === -1) {
    return null;
  }

  // Ensure rectangle is a reasonable size
  const area = (right - left) * (bottom - top);
  const minArea = width * height * 0.15;
  const maxArea = width * height * 0.98;
  if (area < minArea || area > maxArea) {
    return null;
  }

  const quad: Point[] = [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];

  return quad;
}

// Main function: detect document edges
export function detectDocumentEdges(img: HTMLImageElement): Point[] {
  const canvas = document.createElement("canvas");
  
  // Downsample for performance
  const maxDim = 600;
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const width = Math.round(img.width * scale);
  const height = Math.round(img.height * scale);
  
  canvas.width = width;
  canvas.height = height;
  
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return getDefaultCorners(img.width, img.height);
  }
  
  ctx.drawImage(img, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  
  try {
    // Step 1: Convert to grayscale
    const gray = toGrayscale(imageData);
    
    // Step 2: Enhance contrast
    const enhanced = enhanceContrast(gray);

    // First attempt: detect the bright paper area directly
    let quad = findBrightRectangle(enhanced, width, height);
    if (quad) {
      const area = quadArea(quad);
      const minArea = width * height * 0.15;
      const maxArea = width * height * 0.98;

      if (area >= minArea && area <= maxArea && isConvexQuad(quad)) {
        quad = expandQuad(quad, width, height);
        return quad.map((p) => ({
          x: p.x / scale,
          y: p.y / scale,
        }));
      }
      // If not valid, continue with edge-based methods
      quad = null;
    }
    
    // Step 3: Blur to reduce noise
    const blurred = gaussianBlur(enhanced, width, height);
    
    // Step 4: Edge detection
    const magnitude = sobelEdgeDetection(blurred, width, height);
    
    // Step 5: Adaptive thresholding (keep top 5% of edges)
    const edges = adaptiveThreshold(magnitude, 0.05);
    
    // Step 6: Dilate to connect nearby edges
    const dilated = dilate(edges, width, height);

    // Step 7: Try fast projection-based detection first (better for mobile documents)
    quad = findQuadFromProjections(dilated, width, height);

    // Step 8: If projection failed, fall back to Hough line detection
    if (!quad) {
      const lines = houghLines(dilated, width, height);
      quad = findQuadFromLines(lines, width, height);
    }
    
    // Validate the detected quad
    if (quad) {
      const area = quadArea(quad);
      const minArea = width * height * 0.15;
      const maxArea = width * height * 0.98;
      
      if (area < minArea || area > maxArea || !isConvexQuad(quad)) {
        quad = null;
      }
    }
    
    // Step 9: Fallback - try to find bright rectangle (document paper) again
    if (!quad) {
      quad = findBrightRectangle(enhanced, width, height);
      
      if (quad) {
        const area = quadArea(quad);
        const minArea = width * height * 0.15;
        
        if (area < minArea) {
          quad = null;
        }
      }
    }
    
    if (quad) {
      quad = expandQuad(quad, width, height);
      // Scale back to original image coordinates
      return quad.map((p) => ({
        x: p.x / scale,
        y: p.y / scale,
      }));
    }
  } catch (error) {
    console.error("Edge detection error:", error);
  }
  
  // Fallback: return corners with 10% margin
  return getDefaultCorners(img.width, img.height);
}

// A4 aspect ratio: 210mm (width) x 297mm (height) - portrait orientation
// For portrait: height = width * 1.414
const A4_HEIGHT_WIDTH_RATIO = 297 / 210; // ~1.414

function getDefaultCorners(width: number, height: number): Point[] {
  // Calculate A4 rectangle centered in the image with 10% margin
  // A4 is PORTRAIT by default: taller than wide
  const margin = 0.1;
  const availableWidth = width * (1 - margin * 2);
  const availableHeight = height * (1 - margin * 2);
  
  let rectWidth: number;
  let rectHeight: number;
  
  // For portrait A4: height = width * 1.414
  // We need to fit a portrait rectangle
  if (availableWidth * A4_HEIGHT_WIDTH_RATIO <= availableHeight) {
    // Width is the limiting factor - use full available width
    rectWidth = availableWidth;
    rectHeight = rectWidth * A4_HEIGHT_WIDTH_RATIO;
  } else {
    // Height is the limiting factor - use full available height
    rectHeight = availableHeight;
    rectWidth = rectHeight / A4_HEIGHT_WIDTH_RATIO;
  }
  
  // Center the rectangle
  const offsetX = (width - rectWidth) / 2;
  const offsetY = (height - rectHeight) / 2;
  
  return [
    { x: offsetX, y: offsetY },
    { x: offsetX + rectWidth, y: offsetY },
    { x: offsetX + rectWidth, y: offsetY + rectHeight },
    { x: offsetX, y: offsetY + rectHeight },
  ];
}
